import type { ColumnDefinitionInputV1 } from '@/sdk/model/ColumnDefinitionInputV1';
import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import type { ColumnDefinitionOutputV1 } from '@/sdk/model/ColumnDefinitionOutputV1';
import type { TableDefinitionInputV1 } from '@/sdk/model/TableDefinitionInputV1';
import { TableTypeEnum } from '@/sdk/model/TableDefinitionInputV1';
import type { TableDefinitionOutputV1 } from '@/sdk/model/TableDefinitionOutputV1';
import { RecomputeColumnTypeEnum, sqTableDefinitionsApi } from '@/sdk/api/TableDefinitionsApi';
import type { ColumnRuleFormulaCreatorInputV1 } from '@/sdk/model/ColumnRuleFormulaCreatorInputV1';
import type { ColumnRuleInputV1 } from '@/sdk/model/ColumnRuleInputV1';
import type { DatasourceOutputV1 } from '@/sdk/model/DatasourceOutputV1';
import type { GraphQLOutputV1 } from '@/sdk/model/GraphQLOutputV1';
import type { ItemSearchPreviewV1 } from '@/sdk/model/ItemSearchPreviewV1';
import type { RowIdInputV1 } from '@/sdk/model/RowIdInputV1';
import type { TableComputeOutputV1 } from '@/sdk/model/TableComputeOutputV1';
import { sqDatasourcesApi } from '@/sdk/api/DatasourcesApi';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import { sqMaterializedTablesApi } from '@/sdk/api/MaterializedTablesApi';
import { addTableDefinition } from '@/workbook/workbook.actions';
import type { ColumnDefinitionBulkEditInputListV1 } from '@/sdk/model/ColumnDefinitionBulkEditInputListV1';
import type {
  AgGridScalingColumnDefinition,
  BooleanTableCell,
  ColumnDefinition,
  EditorType,
  FormulaCompileResult,
  ItemTableCell,
  MaterializedTableHeader,
  NumericTableCell,
  ProcessedMaterializedTable,
  ProcessedTableCell,
  TextTableCell,
  TreeHierarchyNode,
} from '@/tableDefinitionEditor/tableDefinition.types';
import { runFormula as compileFormulaOrThrowError } from '@/formula/formula.utilities';
import { t } from 'i18next';
import type {
  ColumnRule,
  ColumnRuleInput,
  CombinedColumnRuleInputParameters,
} from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import { ColumnTypeOptions } from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import {
  columnRuleOutputToColumnRuleInput,
  getRuleTypeFromRuleInput,
} from '@/tableDefinitionEditor/columnRules/columnRule.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { setDisplayItemTableEditor, setDisplayTableDefinitionEditor } from '@/worksheet/worksheet.actions';
import _ from 'lodash';
import {
  ADD_TO_TREE_PSEUDO_RULE,
  ColumnRulesWithMetaData,
  CREATE_TREE_PSEUDO_RULE,
  RULES_WITH_PERMISSIONS,
} from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.constants';
import type { ColumnRuleWithMetadata } from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.types';
import { fullyArchiveDatasource } from '@/utilities/datasources.utilities';
import { sqWorkbenchStore } from '@/core/core.stores';
import type { FormulaErrorInterface } from '@/formula/formula.types';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import type { Column, ColumnState, GridApi } from 'ag-grid-community';
import { setDatasources } from '@/search/search.actions';
import type { SelectedSearchItem } from '@/search/search.types';
import { resetItemFinder } from '@/itemFinder/itemFinder.actions';
import type { ColumnDefinitionBulkEditInputV1 } from '@/sdk/model/models';
import { fetchMaterializedTable } from '@/tableDefinitionEditor/materializedTable.utilities';
import type {
  MaterializedTableFilterInput,
  MaterializedTablePropertyColumnInput,
} from '@/tableDefinitionEditor/materializedTable.types';
import {
  CSV_TYPES,
  DATUM_ID_COLUMN,
  DEFAULT_PROPERTIES_FOR_ALL_ITEM_COLUMNS,
  GARBAGE_OBJECT_CHAR,
  ID_COLUMN,
  SCALING_TABLE_DEFINITION_CANCELLATION_GROUP,
} from '@/tableDefinitionEditor/tableDefinition.constants';
import { setItemTableEditorVisible, setTableDefinitionEditorVisible } from '@/main/layout.actions';
import { exposedForTesting } from '@/trendData/trend.actions';
import type { BaseTableDefinitionStore } from '@/tableDefinitionEditor/baseTableDefinitionStore';
import {
  resetTableDefinition,
  setMaterializedTable,
  setModelColumns,
  setTableDefinition,
  setTableDefinitionItemFinder,
} from '@/tableDefinitionEditor/tableDefinition.actions';
import { resetTableEditorStore, setTableId } from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { NULL_PLACEHOLDER } from '@/tableBuilder/tableBuilder.constants';
import type { RuleControl } from '@/tableDefinitionEditor/hooks/useRuleState.hook';
import { resetAssetBuilder } from '@/tableDefinitionEditor/assetBuilder.actions';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import { PUSH_IGNORE } from '@/core/flux.service';
import { getItemFinder } from '@/itemFinder/itemFinder.utilities';

export const getRuleTypeAndParameters = (
  columnRuleInput: ColumnRuleInput,
): {
  ruleType: ColumnRule;
  parameters: Partial<CombinedColumnRuleInputParameters>;
} => {
  const ruleType = getRuleTypeFromRuleInput(columnRuleInput);
  const parameters = columnRuleInput[ruleType]!;
  return { ruleType, parameters };
};

export const getColumnTypeFromText = (text: string): ColumnTypeEnum | undefined => {
  const columnType = ColumnTypeOptions.find((option) => t(option.label) === text);
  return columnType?.value;
};

export const getColumnRuleWithMetaDataFromLabel = (label: string): ColumnRuleWithMetadata | undefined =>
  ColumnRulesWithMetaData.find((rule) => rule.label === label);

export const getColumnRuleWithMetaDataFromRule = (rule: string): ColumnRuleWithMetadata => {
  // In the case of Constant rules, the rule comes back like "stringConstant" whereas the ruleWithLabel is just
  // "constant", causing the exception to be thrown. Instead, we match on whether the rule contains "constant" as a
  // substring
  const columnRule = ColumnRulesWithMetaData.find(
    (ruleWithLabel) =>
      ruleWithLabel.rule === rule ||
      (ruleWithLabel.rule === 'constant' && rule.toLowerCase().includes(ruleWithLabel.rule.toLowerCase())),
  );
  if (!columnRule) {
    throw new Error(`Column rule ${rule} not found`);
  }
  return columnRule;
};

export const buildRowDataForAgGrid = (
  agGridColumnDefinitions: AgGridScalingColumnDefinition[],
  materializedTable: ProcessedMaterializedTable | undefined,
  tableType: TableTypeEnum,
  idsToFilterOut?: string[],
) => {
  if (!materializedTable) {
    return null;
  }

  const columnHeaderForSearchColumn = CSV_TYPES.includes(tableType)
    ? SeeqNames.MaterializedTables.CsvItemIdColumn
    : SeeqNames.MaterializedTables.ItemIdColumn;

  const materializedTableHeaders = materializedTable.headers;
  const indexOfItemIdColumn = materializedTableHeaders.findIndex(
    (header) => header.name === columnHeaderForSearchColumn,
  );
  const rows = idsToFilterOut
    ? materializedTable.rows.filter((row) => {
        // CSV tables can have null in their search column, so we need to check if the row has a valid item ID
        const cell = row[indexOfItemIdColumn];
        const uuid = !cell || !isItemTableCell(cell) ? undefined : cell?.uuid;
        return uuid ? !idsToFilterOut?.includes(uuid) : true;
      })
    : materializedTable.rows;
  return rows.map((row) => {
    const rowData: Record<string, any> = {};
    materializedTableHeaders.forEach((header: any, index: number) => {
      const agColumn = agGridColumnDefinitions.find((columnDef) => columnDef.headerName === header.name);
      const field = agColumn?.field;
      if (field) {
        rowData[field] = row[index];
      }
    });
    return rowData;
  });
};

export const tableDefinitionOutputToTableDefinitionInput = (
  tableDefinition: TableDefinitionOutputV1,
  scopedTo?: string,
): TableDefinitionInputV1 => {
  return {
    name: tableDefinition.name,
    description: tableDefinition.description,
    dataId: tableDefinition.dataId,
    datasourceClass: tableDefinition.datasourceClass,
    datasourceId: tableDefinition.datasourceId,
    scopedTo: tableDefinition.scopedTo,
    subscriptionId: tableDefinition.subscription?.id,
    batchAction: tableDefinition.batchAction,
    columnDefinitions: tableDefinition.columnDefinitions.map((columnDef) =>
      columnDefinitionOutputToColumnDefinitionInput(columnDef, tableDefinition.columnDefinitions),
    ),
    tableType: tableDefinition.tableType,
  };
};

export const columnDefinitionOutputToColumnDefinitionInput = (
  columnDefinition: ColumnDefinitionOutputV1,
  otherColumns: ColumnDefinitionOutputV1[],
): ColumnDefinitionBulkEditInputV1 => {
  return {
    columnId: columnDefinition.id,
    columnType: columnDefinition.columnType,
    columnUom: columnDefinition.columnUom,
    columnName: columnDefinition.columnName,
    columnRules: columnDefinition.rules
      .filter((rule) => !rule.rule.includes(SeeqNames.MaterializedTables.Rules.Overrides.Override))
      .map((rule) => columnRuleOutputToColumnRuleInput(rule, otherColumns)),
    isIndexed: columnDefinition.isIndexed,
    isHidden: columnDefinition.isHidden,
    sortAscending: columnDefinition.sortAscending,
    sortIndex: columnDefinition.sortIndex,
    isGenerated: columnDefinition.isGenerated,
    description: columnDefinition.description,
    displayProperty: columnDefinition.displayProperty,
  };
};

export const removeColumnDefinition = async ({
  tableDefinitionId,
  columnId,
  forceDelete = false,
  updateDetailsPaneItems = false,
}: {
  tableDefinitionId: string;
  columnId: string;
  forceDelete?: boolean;
  updateDetailsPaneItems?: boolean;
}) => {
  if (updateDetailsPaneItems) {
    const tableDefinition = await sqTableDefinitionsApi
      .getTableDefinition({ id: tableDefinitionId })
      .then((res) => res.data);
    // remove the props from item when column is deleted
    const detailsPaneItems = getAllItems({});
    const deletedColumnName = tableDefinition.columnDefinitions.find((col) => col.id === columnId)?.columnName;
    const itemsToUpdate = detailsPaneItems.filter((item) => item.columnName === deletedColumnName);

    itemsToUpdate.forEach((item) => {
      exposedForTesting.setTrendItemProps(
        item.id,
        {
          tableId: undefined,
          tableName: undefined,
          columnName: undefined,
          rowName: undefined,
          rowId: undefined,
          columnId: undefined,
        },
        PUSH_IGNORE,
      );
    });
  }

  const { data: tableDefinitionOutput } = await sqTableDefinitionsApi.deleteColumnFromTableDefinition({
    id: tableDefinitionId,
    columnId,
    forceDelete,
  });

  return tableDefinitionOutput;
};

export const removeColumnOverrides = async (tableDefinitionId: string, columnDefinitionId: string) => {
  const { data: tableDefinitionOutput } = await sqTableDefinitionsApi.deleteOverridesFromColumn({
    id: tableDefinitionId,
    columnId: columnDefinitionId,
  });
  return tableDefinitionOutput;
};

export const addOrUpdateColumnDefinition = async (
  tableDefinitionId: string,
  columnDefinition: ColumnDefinitionBulkEditInputV1,
  columnDefinitionId?: string,
  skipRuleCompute = false,
  includeDetailedResults = true,
): Promise<TableComputeOutputV1 | undefined> => {
  columnDefinition = {
    ...columnDefinition,
    columnId: columnDefinitionId,
  };
  const addOrUpdatePromise = sqTableDefinitionsApi.modifyColumnsInTableDefinition(
    { columnDefinitions: [columnDefinition] },
    {
      id: tableDefinitionId,
      recomputeColumnType: skipRuleCompute
        ? RecomputeColumnTypeEnum.NONE
        : RecomputeColumnTypeEnum.PROVIDEDWITHDEPENDENTS,
      includeDetailedResults,
    },
    { cancellationGroup: tableDefinitionId },
  );

  try {
    const { data: tableComputeOutput } = await addOrUpdatePromise;

    if (columnDefinition.columnRules && columnDefinition.columnRules.some((rule) => rule.treePathCreator)) {
      setDatasources('main', sqWorkbenchStore.stateParams.workbookId, true);
    }
    return tableComputeOutput;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/**
 * When updating, this function will update properties of the table definition except for columns
 */
export const createOrUpdateTableDefinition = async (
  tableDefinition: TableDefinitionInputV1,
  tableDefinitionId?: string,
): Promise<TableDefinitionOutputV1> => {
  const { data: tableDefinitionOutput } = await (tableDefinitionId
    ? sqTableDefinitionsApi.updateTableDefinition(tableDefinition, { id: tableDefinitionId })
    : sqTableDefinitionsApi.createTableDefinition(tableDefinition));
  addTableDefinition(tableDefinitionOutput);

  return tableDefinitionOutput;
};

export const updateTableDefinitionName = async ({
  tableDefinitionId,
  name,
}: {
  tableDefinitionId: string;
  name: string;
}) => await sqItemsApi.setProperty({ value: name }, { id: tableDefinitionId, propertyName: SeeqNames.Properties.Name });

export const deleteTableRows = async (tableDefinitionId: string, rowIds: object[]) => {
  try {
    const response = await sqMaterializedTablesApi.deleteRows(
      {
        rows: rowIds as RowIdInputV1[],
      },
      {
        tableId: tableDefinitionId,
      },
    );
    return response.data;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
    return {};
  }
};

export const getMaterializedTable = async (
  tableDefinitionId: string,
  columnsToInclude?: string[],
  propertiesToInclude?: MaterializedTablePropertyColumnInput[],
  cancellationGroup?: string,
  limit?: number,
  cursorKey?: string,
  filter?: MaterializedTableFilterInput,
): Promise<GraphQLOutputV1> => {
  const response = await fetchMaterializedTable({
    tableDefinitionId,
    columnsToInclude,
    propertiesToInclude,
    includeOverrides: true,
    cancellationGroup,
    limit,
    cursorKey,
    filter,
  });
  if (response.errors) {
    errorToast({ httpResponseOrError: response });
  }
  return response;
};

/**
 * Validates a formula for an instance of a formulaCreatorRule. In order to compile the formula, this function will
 * find the first viable item in each column that the formula references and use those as parameters. If no viable
 * items are found, the function will return an error. If viable parameters are found but there is a compilation
 * error, the compilation error will be returned.
 */
export const validateFormulaForCreator = async (
  formulaRule: ColumnRuleFormulaCreatorInputV1,
  materializedTable?: ProcessedMaterializedTable,
  columnDefinitions?: ColumnDefinitionOutputV1[],
): Promise<FormulaCompileResult> => {
  if (!materializedTable || !columnDefinitions || columnDefinitions.length === 0) {
    return {
      success: false,
      errors: [{ message: 'No items in the table to run the formula on', column: -1, line: -1 }],
    };
  }
  const errors: FormulaErrorInterface[] = [];
  const materializedTableHeaders = materializedTable.headers;
  const parameters = formulaRule.parameters || [];
  const columnIndexes = formulaRule.columnIndexes || [];

  const parametersForCompile = parameters
    .map((parameter, index) => {
      const indexOfColumnInTableDefinition = columnIndexes[index] - 1;
      const columnName = columnDefinitions[indexOfColumnInTableDefinition].columnName;
      const indexOfColumnInMaterializedTable = materializedTableHeaders.findIndex(
        (header) => header.name === columnName,
      );
      const rowWithValidItem = materializedTable.rows.find((row) =>
        isItemTableCell(row[indexOfColumnInMaterializedTable]),
      );
      const idOfFirstValidItemInGivenColumn = rowWithValidItem
        ? (rowWithValidItem[indexOfColumnInMaterializedTable] as ItemTableCell).uuid
        : undefined;

      if (idOfFirstValidItemInGivenColumn === undefined && formulaRule.formula.includes(parameter)) {
        errors.push({
          message: `No valid items in column ${columnName} which is referred to by parameter: ${parameter}`,
          column: -1,
          line: -1,
        });
      }
      return `${parameter}=${idOfFirstValidItemInGivenColumn}`;
    })
    .filter((param) => param.includes('undefined') === false);

  if (errors.length > 0) {
    return { success: false, errors };
  }

  let formulaResult = formulaRule.formula;
  formulaRule.variableParameters?.forEach((parameter, index) => {
    const variableParams = formulaRule.variableParameterStrings
      ?.at(index)
      ?.split(',')
      ?.map((param) => param.trim())
      .map((param) => param.replace('$', ''))
      .filter((param) => parametersForCompile.some((paramForCompile) => paramForCompile.includes(`${param}=`)))
      .map((param) => `$${param}`);
    formulaResult = `$${parameter}=parameters(${variableParams?.join(',')})\n${formulaResult}`;
  });
  try {
    await compileFormulaOrThrowError(formulaResult, parametersForCompile);
    return { success: true, errors: [] };
  } catch (e: any) {
    return { success: false, errors: e };
  }
};

export async function getExistingTableDefinition(tableDefinitionId: string, editorType: EditorType) {
  try {
    const { data: tableDefinitionData } = await sqTableDefinitionsApi.getTableDefinition({ id: tableDefinitionId });
    setTableDefinition(editorType, tableDefinitionData);

    await getTableModelApplications(tableDefinitionId, editorType);

    return tableDefinitionData;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
}

export async function editTableDefinition(tableDefinition: SelectedSearchItem, editorType: EditorType) {
  resetItemFinder();
  resetTableDefinition(editorType);
  resetTableEditorStore(editorType);
  try {
    const { data: tableDefinitionData } = await sqTableDefinitionsApi.getTableDefinition(
      { id: tableDefinition.id },
      {
        cancellationGroup: SCALING_TABLE_DEFINITION_CANCELLATION_GROUP,
      },
    );
    setTableId(editorType, tableDefinitionData.id);
    setTableDefinition(editorType, tableDefinitionData);
    if (editorType === 'SCALING') {
      setDisplayTableDefinitionEditor(true);
    } else if (editorType === 'ITEM') {
      setDisplayItemTableEditor(true);
    }

    const subscriberId = tableDefinitionData.subscription?.id;
    if (subscriberId) {
      await getTableDefinitionItemFinder(editorType, subscriberId);
    }
    await getTableModelApplications(tableDefinition.id, editorType);
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
}

export async function getTableModelApplications(tableDefinitionId: string, editorType: EditorType) {
  try {
    const { data: modelData } = await sqTableDefinitionsApi.getTableDefinitionModelApplications({
      id: tableDefinitionId,
    });

    if (modelData.applications?.length) {
      setModelColumns(editorType, modelData);
    }
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
}

export const isItemTableCell = (cell: any): cell is ItemTableCell => {
  return cell && cell.uuid && typeof cell.uuid === 'string';
};

export const isNumericTableCell = (cell: any): cell is NumericTableCell => {
  return cell && typeof cell.value === 'number';
};

export const isTextTableCell = (cell: any): cell is TextTableCell => {
  return cell && typeof cell.value === 'string';
};

export const isBooleanTableCell = (cell: any): cell is BooleanTableCell => {
  return cell && typeof cell.value === 'boolean';
};

/**
 * Retrieves the data source for a table definition.
 */
export const getTableDefinitionDataSource = async (
  tableDefinitionId: string,
): Promise<DatasourceOutputV1 | undefined> => {
  const {
    data: { datasources },
  } = await sqDatasourcesApi.getDatasources({
    datasourceId: tableDefinitionId.toLocaleLowerCase(),
  });

  return datasources?.at(0);
};

/**
 * Deletes datasource associated with a table definition which
 * will delete all created items associated with the table definition.
 */
export async function deleteTableItems(tableDefinitionId: string): Promise<void> {
  const tableDefinitionDatasource = await getTableDefinitionDataSource(tableDefinitionId);
  if (tableDefinitionDatasource) {
    await fullyArchiveDatasource(tableDefinitionDatasource.id);
  }
}

export const getColumnsContainingRulesWithPermissions = (
  columns: ColumnDefinitionOutputV1[],
): ColumnDefinitionOutputV1[] =>
  columns.filter((column) => column.rules.some((rule) => RULES_WITH_PERMISSIONS.includes(rule.rule)));

export const addOrUpdateColumnDefinitions = async (
  columnsToUpdate: ColumnDefinitionBulkEditInputV1[],
  tableDefinitionId: string,
  skipRuleCompute = false,
  includeDetailedResults = true,
): Promise<TableComputeOutputV1 | undefined> => {
  if (_.size(columnsToUpdate) <= 0) {
    return undefined;
  }
  try {
    const { data: tableComputeOutput } = await sqTableDefinitionsApi.modifyColumnsInTableDefinition(
      { columnDefinitions: columnsToUpdate },
      {
        id: tableDefinitionId,
        recomputeColumnType: skipRuleCompute
          ? RecomputeColumnTypeEnum.NONE
          : RecomputeColumnTypeEnum.PROVIDEDWITHDEPENDENTS,
        includeDetailedResults,
      },
      { cancellationGroup: tableDefinitionId },
    );

    return tableComputeOutput;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

const updateColumnRuleScope = (columnRules: ColumnRuleInputV1[], scopedTo?: string): ColumnRuleInputV1[] => {
  return columnRules.map((columnRule) => {
    const ruleType = Object.keys(columnRule)[0] as keyof ColumnRuleInputV1;
    if (RULES_WITH_PERMISSIONS.includes(ruleType)) {
      return {
        [ruleType]: {
          ...columnRule[ruleType],
          scopedTo,
        },
      };
    }
    return columnRule;
  });
};

/**
 * Updates the columns containing rules with permissions with the new
 * access settings
 */
export const updateColumnsContainingRulesWithScope = async (
  tableDefinition: TableDefinitionOutputV1,
  editorType: EditorType,
  workbookId?: string,
): Promise<TableDefinitionOutputV1 | undefined> => {
  const columnsToUpdate = getColumnsContainingRulesWithPermissions(tableDefinition.columnDefinitions).map((column) =>
    columnDefinitionOutputToColumnDefinitionInput(column, tableDefinition.columnDefinitions),
  );
  const columnsToUpdateWithUpdatedRules = columnsToUpdate.map((column) => ({
    ...column,
    columnRules: updateColumnRuleScope(column.columnRules, tableDefinition.scopedTo),
  }));

  if (columnsToUpdateWithUpdatedRules.some((column) => column.columnRules.some((rule) => rule.treePathCreator))) {
    await setDatasources('main', workbookId, true);
  }

  try {
    const columnUpdate = await addOrUpdateColumnDefinitions(
      columnsToUpdateWithUpdatedRules,
      tableDefinition.id,
      false,
      true,
    );

    return _.isEmpty(columnsToUpdateWithUpdatedRules) ? tableDefinition : columnUpdate?.tableDefinition;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/**
 * Updates the visibility of columns in a table based on user actions.
 *
 * @param tableDefinitionId
 * @param existingColumnsAsOutputs
 * @param existingColumnsAsInputs
 * @param agColumns - The AgGrid columns pulled from the api at time of execution
 * @param setTableDefinition - set the table definition in the store
 * @param editorType - the editor type of the table
 * @returns Promise<void>
 */
export const onColumnHideTriggeredByUser = async (
  tableDefinitionId: string,
  existingColumnsAsOutputs: ColumnDefinition[],
  existingColumnsAsInputs: ColumnDefinitionInputV1[],
  agColumns: Column[],
  setTableDefinition: (editorType: EditorType, tableDefinitionOutput: TableDefinitionOutputV1) => void,
  editorType: EditorType,
) => {
  const columnIdToColumnInputMap: Record<string, ColumnDefinitionInputV1> = existingColumnsAsOutputs.reduce(
    (acc, column, index) => {
      acc[column.id] = existingColumnsAsInputs[index];
      return acc;
    },
    {} as Record<string, ColumnDefinitionInputV1>,
  );

  const tableColumnDefinitionsWithUpdatedVisibility: ColumnDefinitionBulkEditInputV1[] = (
    agColumns
      .map((agColumn) => {
        const columnId = agColumn.getColDef().field;
        const columnInput = columnId !== undefined ? columnIdToColumnInputMap[columnId] : undefined;
        const visibilityChanged = columnInput && !columnInput?.isHidden !== agColumn.isVisible();
        return visibilityChanged
          ? {
              isVisibleNow: agColumn.isVisible(),
              columnInput,
              columnId,
            }
          : undefined;
      })
      .filter((agColumn) => !!agColumn) as {
      isVisibleNow: boolean;
      columnInput: ColumnDefinitionInputV1;
      columnId: string;
    }[]
  ).map((agColumn) => {
    const columnInput = agColumn.columnInput;
    return {
      ...columnInput,
      isHidden: !agColumn.isVisibleNow,
      columnId: agColumn.columnId,
    } as ColumnDefinitionBulkEditInputV1;
  });

  const updatedTableDefinition = await addOrUpdateColumnDefinitions(
    tableColumnDefinitionsWithUpdatedVisibility,
    tableDefinitionId,
    true,
    false,
  );
  if (updatedTableDefinition) {
    setTableDefinition(editorType, updatedTableDefinition.tableDefinition);
  }
};

/**
 * Updates the column definitions based on the user's sorting changes in the table.
 *
 * @param tableDefinitionId
 * @param existingColumnsAsOutputs
 * @param existingColumnsAsInputs
 * @param agColumns The AgGrid columns pulled from the api at time of execution
 * @param editorType - the editor type of the table
 */
export const onSortChangedByUser = async (
  tableDefinitionId: string,
  existingColumnsAsOutputs: ColumnDefinition[],
  existingColumnsAsInputs: ColumnDefinitionInputV1[],
  agColumns: Column[],
  editorType: EditorType,
) => {
  const columnIdToColumnInputMap: Record<string, ColumnDefinitionInputV1> = existingColumnsAsOutputs.reduce(
    (acc, column, index) => {
      acc[column.id] = existingColumnsAsInputs[index];
      return acc;
    },
    {} as Record<string, ColumnDefinitionInputV1>,
  );
  const tableDefColumnsWithUpdatedSort = agColumns
    .map((agColumn) => {
      const columnId = agColumn.getColDef().field;
      const columnInput = columnId !== undefined ? columnIdToColumnInputMap[columnId] : undefined;
      const existingSortIndex = columnInput?.sortIndex;
      const existingPotentialSort = columnInput?.sortAscending ? 'asc' : 'desc';
      const existingSort = existingSortIndex !== undefined ? existingPotentialSort : undefined;
      const agSort = agColumn.getSort();
      const agSortIndex = agColumn.getSortIndex() ?? undefined;
      if (existingSort !== agSort || existingSortIndex !== agSortIndex) {
        return {
          ...columnInput,
          sortAscending: agSort === 'asc',
          sortIndex: agSortIndex,
          columnId,
        } as ColumnDefinitionBulkEditInputV1;
      }
      return undefined;
    })
    .filter((column) => !!column) as ColumnDefinitionBulkEditInputV1[];
  const updatedTableDefinition = await addOrUpdateColumnDefinitions(
    tableDefColumnsWithUpdatedSort,
    tableDefinitionId,
    true,
  );
  if (updatedTableDefinition) {
    setTableDefinition(editorType, updatedTableDefinition.tableDefinition);
  }
};

export const onColumnMovedByUser = async (
  tableDefinitionId: string,
  columns: ColumnDefinition[],
  agGridColumns: Column[],
  affectedColumnId: string,
  toIndex: number,
  editorType: EditorType,
) => {
  const columnsIdsNotInGrid = columns
    .filter((column) => !agGridColumns.find((agGridColumn) => agGridColumn.getColDef().field === column.id))
    .map((column) => column.id);

  const columnsIdsInGridInNewOrder = agGridColumns
    .map((agColumn) => agColumn.getColDef().field)
    .filter((columnId) => columnId !== affectedColumnId)
    .filter((columnId): columnId is string => columnId !== undefined);

  columnsIdsInGridInNewOrder.splice(toIndex, 0, affectedColumnId);
  const newOrder = columnsIdsNotInGrid.concat(columnsIdsInGridInNewOrder);
  const { data: updatedTableDefinition } = await sqTableDefinitionsApi.reorderColumnsInTableDefinition(
    { columnDefinitionIds: newOrder },
    { id: tableDefinitionId },
  );
  setTableDefinition(editorType, updatedTableDefinition);
};

export const getColumnInputBasedOnColumnName = (
  tableDefinition: TableDefinitionInputV1,
  name?: string,
): ColumnDefinitionInputV1 | undefined => {
  return tableDefinition.columnDefinitions.find((column) => column.columnName === name);
};

export const updateSortsInTheGrid = (tableDefinition: TableDefinitionOutputV1, agGridApi: GridApi) => {
  const columnStateForUpdatedSorts: ColumnState[] = tableDefinition.columnDefinitions
    .map((columnDefinition) => {
      const agColumn = agGridApi.getColumn(columnDefinition.id);
      const sortIndexFromAgColumn = agColumn?.getSortIndex();
      const sortDirection = agColumn?.getSort();
      const sortDirectionIsAscending = sortDirection === 'asc';

      const sortAscendingIsEqual = sortDirectionIsAscending === columnDefinition.sortAscending;
      const sortIndexIsEqual = sortIndexFromAgColumn === columnDefinition.sortIndex;
      if (!sortIndexIsEqual || !sortAscendingIsEqual) {
        const sortingIsOff = columnDefinition.sortIndex === undefined;
        const potentialSortDirection = columnDefinition.sortAscending ? 'asc' : 'desc';
        const sort = sortingIsOff ? null : potentialSortDirection;
        return {
          colId: columnDefinition.id,
          sort,
          sortIndex: columnDefinition.sortIndex ?? null,
        };
      } else {
        return undefined;
      }
    })
    .filter((col) => !!col) as ColumnState[];
  if (columnStateForUpdatedSorts.length > 0) {
    agGridApi.applyColumnState({ state: columnStateForUpdatedSorts });
  }
};

export const updateVisibilityInTheGrid = (tableDefinition: TableDefinitionOutputV1, agGridApi: GridApi) => {
  const columnStateForVisibilityChanged: ColumnState[] = tableDefinition.columnDefinitions
    .map((columnDefinition) => {
      const agColumn = agGridApi.getColumn(columnDefinition.id);
      const isHidden = !agColumn?.isVisible();
      if (columnDefinition.isHidden !== isHidden) {
        return {
          colId: columnDefinition.id,
          hide: columnDefinition.isHidden,
        };
      } else {
        return undefined;
      }
    })
    .filter((col) => !!col) as ColumnState[];
  if (columnStateForVisibilityChanged.length > 0) {
    agGridApi.applyColumnState({ state: columnStateForVisibilityChanged });
  }
};

export const generateTableName = (
  currentId: string,
  tablesInLocal: ItemSearchPreviewV1[],
  defaultName: string,
  currentName?: string,
): string => {
  if (!currentId && !currentName) {
    return `${t(defaultName)} ${tablesInLocal.length + 1}`;
  }
  return currentName || `${t(defaultName)} ${tablesInLocal.length + 1}`;
};

export const closeTableEditor = (editor: EditorType) => {
  if (editor === 'SCALING') {
    setDisplayTableDefinitionEditor(false);
    setTableDefinitionEditorVisible(false);
  } else {
    setDisplayItemTableEditor(false);
    setItemTableEditorVisible(false);
  }
  resetAssetBuilder();
  resetTableDefinition(editor);
  resetItemFinder();
  resetTableEditorStore(editor);
};

/**
 * Uses {@link RuleControl} to construct the final rule state and outputs the rule being edited if the save is
 * successful, otherwise undefined. Updates ruleControl to facilitate changes in the rule UI throughout the
 * component stack.
 * The returned {@link ColumnRuleInputV1} needs to be
 * saved to local state or otherwise consumed, as state will be cleared at the end of a successful operation.
 */
export const createOrUpdateColumnRule = async (
  ruleControl: RuleControl,
  materializedTable: ProcessedMaterializedTable | undefined,
  allColumnDefinitions: ColumnDefinition[],
  skipFormulaValidation: boolean,
  savePseudoRuleRef: React.MutableRefObject<() => Promise<void>>,
): Promise<ColumnRuleInputV1 | undefined> => {
  const {
    canSave,
    columnRuleWithMetadata,
    ruleParametersState,
    setShowError,
    setColumnRuleWithMetadata,
    ruleParametersDispatch,
    setFormulaCompileResults,
  } = ruleControl;
  if (!canSave) {
    setShowError(true);
    return undefined;
  }
  await savePseudoRuleRef.current?.();
  let columnRule: ColumnRuleInputV1 = {
    [columnRuleWithMetadata!.rule]: ruleParametersState,
  };

  if (
    columnRuleWithMetadata!.rule === CREATE_TREE_PSEUDO_RULE ||
    columnRuleWithMetadata!.rule === ADD_TO_TREE_PSEUDO_RULE
  ) {
    columnRule = {
      treePathCreator: ruleParametersState,
    } as ColumnRuleInputV1;
  }

  const formulaCompileResults = await (columnRuleWithMetadata!.rule ===
    SeeqNames.MaterializedTables.Rules.FormulaCreator && !skipFormulaValidation
    ? validateFormulaForCreator(
        ruleParametersState as ColumnRuleFormulaCreatorInputV1,
        materializedTable,
        allColumnDefinitions,
      )
    : Promise.resolve({ success: true, errors: [] }));
  setFormulaCompileResults(formulaCompileResults);
  if (!formulaCompileResults.success) {
    return undefined;
  }

  ruleParametersDispatch('reset');
  setShowError(false);
  setColumnRuleWithMetadata(undefined);
  return columnRule;
};

/**
 * Returns the icon for the given types (type + uom) for AG grid column menu display. If all types are the same, it
 * returns the need ag grid icon element for that type.
 * @param types
 */
export const getIconForTypes = (types: { type: string | undefined; uom: any }[]): string => {
  if (types.length > 0 && types.every((type) => type.type === types[0].type && type.uom === types[0].uom)) {
    return getIconForType(types[0].type, types[0].uom);
  }
  return '';
};

/**
 * Returns the icon classes for the given type and uom.
 * @param type
 * @param uom
 */
export const getIconForType = (type: string | undefined, uom: any): string => {
  switch (type) {
    case SeeqNames.Types.CalculatedSignal:
    case SeeqNames.Types.StoredSignal:
      return uom !== 'string' ? 'fa-sharp fa-regular fa-fw fc fc-series' : 'fa-sharp fa-regular fa-fw fa fa-font';
    case SeeqNames.Types.CalculatedCondition:
    case SeeqNames.Types.StoredCondition:
      return 'fa-sharp fa-regular fa-fw fc fc-capsule-set';
    case SeeqNames.Types.LiteralScalar:
    case SeeqNames.Types.CalculatedScalar:
      return 'fa-sharp fa-regular fa-fw fc fc-scalar';
    case SeeqNames.Types.ThresholdMetric:
      return 'fa-sharp fa-regular fa-fw fc fc-metric';
    default:
      return '';
  }
};

/**
 * Extracts the parent level from an event property rule string. The string should contain at most one instance of
 * parent[<parentLevel>].
 * @param string
 */
export const extractAncestorLevelFromString = (string: string) => {
  const ancestorRegex = new RegExp('parent\\[(\\d+)]|ancestor\\[(\\d+)]');
  const matches: string[] | null = string.match(ancestorRegex);
  if (matches && matches.length === 3 && !isNaN(Number(matches[2] || matches[1]))) {
    return Number(matches[2] || matches[1]);
  } else {
    return undefined;
  }
};

/**
 * Extracts the ancestor from an event property rule string. The string should contain at most one instance of
 * parent[<parentLevel>] or ancestor[<ancestorLevel>].
 * @param string
 */
export const extractAncestorFromString = (string: string) => {
  const ancestorRegex = new RegExp('(parent\\[\\d+])|(ancestor\\[(\\d+))]');
  const matches: string[] | null = string.match(ancestorRegex);
  if (matches && matches.length >= 1) {
    return matches[0];
  } else {
    return '';
  }
};

/**
 * Extracts the child key from an event property rule string. The string should contain at most one instance of
 * children[<childKey>].
 * @param string
 */
export const extractChildKeyFromString = (string: string) => {
  const childrenRegex = new RegExp('children\\[(.+?)]');
  const matches: string[] | null = string.match(childrenRegex);
  if (matches && matches.length === 2) {
    return matches[1];
  } else {
    return undefined;
  }
};

/**
 * Extracts the property name from an event property rule string. If no parent[<level>] or children[<childKey>] elements
 * are found, the string is returned indicating a property on the search items source level
 * @param string
 */
export const extractPropertyNameFromString = (string: string) => {
  const propertyNameRegex = new RegExp(
    '(?:parent\\[\\d+]|children\\[.+?]|ancestor\\[\\d+])(?:\\.(?:parent\\[\\d+]|children\\[.+?]|ancestor\\[\\d+]))*\\.(.+)',
  );
  const matches: string[] | null = string.match(propertyNameRegex);
  if (matches && matches.length === 2) {
    return matches[1];
  } else {
    return string;
  }
};

/**
 * Checks if the ancestor column is an item column. This is used to determine if asset icon should display and if
 * the property ID should be used for the event property rule.
 * @param eventNodes
 * @param levelAccessor
 * @param tableDefinitionStore
 */
export const isAncestorColumnAnItem = (
  eventNodes: TreeHierarchyNode[],
  levelAccessor: string,
  tableDefinitionStore: BaseTableDefinitionStore,
) => {
  return !eventNodes
    .map((node) => {
      return tableDefinitionStore.columnOptions?.hierarchy?.get(levelAccessor)?.type !== undefined;
    })
    .some((hasItemType) => hasItemType === false);
};

/**
 * Returns the ancestor rules for the given event nodes in the source column. The ancestor rules are calculated into
 * event property rules based on the event node's event accessor and converting to the appropriate level for the rules.
 * @param eventNodes
 * @param levelAccessor
 * @param isItemColumn
 * @param tableDefinitionStore
 */
export const getAncestorRules = (eventNodes: TreeHierarchyNode[], levelAccessor: string, isItemColumn: boolean) => {
  return eventNodes.map((node) => {
    const ancestorLevel = extractAncestorLevelFromString(levelAccessor);

    const propertyName = isItemColumn ? SeeqNames.Properties.Id : SeeqNames.Properties.Name;

    return {
      eventProperty: {
        propertyName: ancestorLevel !== undefined ? `${levelAccessor}.${propertyName}` : `${propertyName}`,
      },
    };
  });
};

/**
 * Returns the property rules for the given event nodes in the source column.
 * @param eventNodes
 * @param name
 * @param columnIndex
 */
export const getPropertyRules = (eventNodes: TreeHierarchyNode[], name: string, columnIndex: number) => {
  if (eventNodes === undefined || eventNodes.length === 0) {
    return [
      {
        findProperty: {
          columnIndex,
          propertyName: `${name}`,
        },
      },
    ];
  }
  return eventNodes.map((node) => {
    const levelAccessor = node.eventAccessor === '' ? '' : `${node.eventAccessor}.`;
    return {
      eventProperty: {
        propertyName: `${levelAccessor}${name}`,
      },
    };
  });
};

/**
 * Returns the descendant rules for the given event nodes in the source column.
 * @param eventNodes
 * @param child
 */
export const getDescendantRules = (eventNodes: TreeHierarchyNode[], child: string) => {
  return eventNodes.map((node) => {
    const levelAccessor = node.eventAccessor === '' ? '' : `${node.eventAccessor}.`;
    return {
      eventProperty: {
        propertyName: `${levelAccessor}children[${child}].${SeeqNames.Properties.Id}`,
      },
    };
  });
};

export const getScalingTableTypes = () => [TableTypeEnum.SCALING, TableTypeEnum.SCALINGCSV];
export const getItemTableTypes = () => [TableTypeEnum.ITEM, TableTypeEnum.ITEMCSV];

/**
 * Returns the event nodes for the given materialized table column definition. The event nodes used to determine the
 * rules generated from the column shortcuts.
 * @param mtColumnDefinition
 * @param tableDefinitionStore
 */
export const findEventNodes = (
  mtColumnDefinition: ColumnDefinition,
  tableDefinitionStore: BaseTableDefinitionStore,
) => {
  return mtColumnDefinition.rules
    .filter((rule) => rule.rule === SeeqNames.MaterializedTables.Rules.EventProperty)
    .map((rule) => rule.arguments[SeeqNames.MaterializedTables.Parameters.PropertyName])
    .map((property) => {
      const childKey = extractChildKeyFromString(property);
      const ancestorAccessor = extractAncestorFromString(property);

      let eventNode = tableDefinitionStore.columnOptions?.hierarchy?.get(ancestorAccessor);
      if (childKey) {
        eventNode = eventNode?.children?.get(childKey);
      }
      return eventNode;
    })
    .filter((node) => node !== undefined) as TreeHierarchyNode[];
};

export interface ColumnAncestorLevel {
  level: number;
  key: string;
}

export const getColumnAncestorLevels = (
  mtColumnDefinition: ColumnDefinition,
  tableDefinitionStore: BaseTableDefinitionStore,
): Set<ColumnAncestorLevel> => {
  return new Set(
    mtColumnDefinition.rules
      .filter((rule) => rule.rule === SeeqNames.MaterializedTables.Rules.EventProperty)
      .map((rule) => rule.arguments[SeeqNames.MaterializedTables.Parameters.PropertyName])
      .flatMap((property) => {
        const searchItemsLevel =
          tableDefinitionStore.tableType === TableTypeEnum.SCALING
            ? 0
            : Math.max(
                ...Array.from(tableDefinitionStore.columnOptions?.hierarchy?.keys() ?? []).map((key) => {
                  return extractAncestorLevelFromString(key) ?? 0;
                }),
              ) + 1;
        const ancestorLevel = extractAncestorLevelFromString(property) ?? searchItemsLevel;
        const childKey = extractChildKeyFromString(property);
        const childModifier = tableDefinitionStore.tableType === TableTypeEnum.SCALING ? 1 : 0;
        const ancestorModifier = tableDefinitionStore.tableType === TableTypeEnum.SCALING ? -ancestorLevel : 0;

        return Array.from(tableDefinitionStore.columnOptions?.hierarchy?.keys() ?? [])
          .filter((key) => {
            if (childKey) {
              return tableDefinitionStore.tableType === TableTypeEnum.SCALING
                ? (extractAncestorLevelFromString(key) ?? searchItemsLevel) >= ancestorLevel
                : (extractAncestorLevelFromString(key) ?? searchItemsLevel) <= ancestorLevel;
            } else {
              return tableDefinitionStore.tableType === TableTypeEnum.SCALING
                ? (extractAncestorLevelFromString(key) ?? searchItemsLevel) > ancestorLevel
                : (extractAncestorLevelFromString(key) ?? searchItemsLevel) < ancestorLevel;
            }
          })
          .map((key) => {
            if (childKey) {
              return {
                level: (extractAncestorLevelFromString(key) ?? searchItemsLevel) + childModifier + ancestorModifier,
                key,
              } as ColumnAncestorLevel;
            }
            return {
              level: (extractAncestorLevelFromString(key) ?? searchItemsLevel) + ancestorModifier,
              key,
            } as ColumnAncestorLevel;
          });
      }),
  );
};

export const hasDescendantColumnOptions = (
  mtColumnDefinition: ColumnDefinition,
  tableDefinitionStore: BaseTableDefinitionStore,
): boolean => {
  if (!mtColumnDefinition) {
    return false;
  }
  const eventNodes = findEventNodes(mtColumnDefinition, tableDefinitionStore);
  const childrenOptions: Set<string> = new Set(eventNodes.flatMap((node) => Array.from(node.children?.keys() ?? [])));
  return childrenOptions.size > 0;
};

export const hasAncestorColumnOptions = (
  mtColumnDefinition: ColumnDefinition,
  tableDefinitionStore: BaseTableDefinitionStore,
): boolean => {
  if (!mtColumnDefinition) {
    return false;
  }
  const ancestorLevelOptions: Set<ColumnAncestorLevel> = getColumnAncestorLevels(
    mtColumnDefinition,
    tableDefinitionStore,
  );
  return Array.from(ancestorLevelOptions).length > 0;
};

export const consolidatedValueFormatter = (params: { value: ProcessedTableCell; colDef?: any }): string => {
  const { value, colDef } = params;
  if (isItemTableCell(value)) {
    const displayProperty = colDef?.context?.displayProperty;
    const fetchedProperties = value.fetchedProperties;
    const propertyValue = fetchedProperties?.[displayProperty];
    if (displayProperty && value.fetchedProperties) {
      return _.isNil(propertyValue) || propertyValue === GARBAGE_OBJECT_CHAR ? NULL_PLACEHOLDER : propertyValue;
    }

    return value?.uuid ?? NULL_PLACEHOLDER;
  }

  if (isBooleanTableCell(value)) {
    return value.value ? 'true' : 'false';
  }

  if (isNumericTableCell(value)) {
    return _.isNil(value?.value) ? NULL_PLACEHOLDER : `${value?.value}${value?.uom ?? ''}`;
  }

  if (isTextTableCell(value)) {
    return value?.value !== undefined && value.value !== GARBAGE_OBJECT_CHAR ? value.value : NULL_PLACEHOLDER;
  }

  return NULL_PLACEHOLDER;
};

export const getColumnsNotIncludedInAgGrid = (
  tableType: TableTypeEnum,
  columns: ColumnDefinitionOutputV1[],
): string[] => {
  if (!CSV_TYPES.includes(tableType)) {
    return [SeeqNames.MaterializedTables.DatumIdColumn];
  }
  if (
    CSV_TYPES.includes(tableType) &&
    columns.some((column) => column.columnName === SeeqNames.MaterializedTables.CsvItemIdColumn)
  ) {
    return [SeeqNames.MaterializedTables.ItemIdColumn];
  }

  return [];
};

export const addOrUpdatePreviewColumns = async (
  tableDefinitionId: string,
  previewColumns: ColumnDefinitionBulkEditInputListV1,
  editorType: EditorType,
) => {
  const addOrUpdatePromise = sqTableDefinitionsApi.previewTableDefinition(
    {
      columnDefinitions: previewColumns,
      bypassModelControlledRestriction: true,
    },
    {
      id: tableDefinitionId,
    },
    { cancellationGroup: tableDefinitionId },
  );

  try {
    const { data: tableComputeOutput } = await addOrUpdatePromise;

    const materializedTable = {
      headers: tableComputeOutput.table.headers as unknown as MaterializedTableHeader[],
      rows: tableComputeOutput.table.rows,
      hasMore: false,
    };

    const columns = tableComputeOutput.columnDefinitions.map((column) => {
      const displayName = getColumnDisplayName(column.columnName, undefined);
      const displayProperty = column.columnType === ColumnTypeEnum.UUID ? column?.displayProperty : null;
      const isUUIDColumn = column.columnType === ColumnTypeEnum.UUID;
      const additionalProperties = isUUIDColumn ? DEFAULT_PROPERTIES_FOR_ALL_ITEM_COLUMNS : undefined;
      return {
        additionalProperties,
        displayName,
        ...(displayProperty !== null && { displayProperty }),
        ...column,
      };
    });

    setMaterializedTable(editorType, materializedTable, columns);

    return columns;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

export const getColumnDisplayName = (columnName: string, uniqueIDColumnName: string | undefined): string => {
  if (columnName === DATUM_ID_COLUMN.columnName) {
    return uniqueIDColumnName ?? t('SCALING.DEFAULT_DATUM_ID_COLUMN_NAME');
  }

  if (columnName === SeeqNames.MaterializedTables.CsvItemIdColumn) {
    return t('SCALING.DEFAULT_ITEM_ID_COLUMN_NAME');
  }

  if (columnName === ID_COLUMN.columnName) {
    return t('SCALING.DEFAULT_ITEM_ID_COLUMN_NAME');
  }

  return columnName;
};

export const getTableDefinitionItemFinder = async (editorType: EditorType, subscriberId: string) => {
  const itemFinder = await getItemFinder(subscriberId);
  setTableDefinitionItemFinder(editorType, itemFinder);
};
